import React, { useState, useMemo } from "react";
import { Modal } from "../../components/Modal";
import { CreditCard } from "../../components/shared/CreditCard";
import { Typography } from "../../components/shared/Typography";
import { useCardData } from "../../hooks/useCardData";
import { useCreditData } from "../../hooks/useCreditData";
import {
  useCardDetails,
  CardDetailsCallbacks,
} from "../../hooks/useCardDetails";
import ChevronRightIcon from "../../components/icons/ChevronRightIcon";
import { UserCard } from "../../api/types";
import {
  InfoIcon,
  ContentWrapper,
  WalletTag,
  FreezeCardTransactions,
  CustomButton,
  CustomInput,
} from "./components";

type ViewType = "main" | "changePin" | "transactionLimit";

interface ManageCardModalProps {
  title: string;
  onClose: () => void;
  callbacks: CardDetailsCallbacks;
  address: string;
  chainId: number;
}

export function ManageCardModal({
  title,
  onClose,
  callbacks,
  address,
  chainId,
}: ManageCardModalProps) {
  const { creditInfo } = useCreditData();
  const { userCard } = useCardData();
  const [currentView, setCurrentView] = useState<ViewType>("main");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);

  const {
    pin,
    limit,
    inputLimit,
    expiry,
    showCardDetails,
    decryptedCardNumber,
    decryptedCVC,
    isLoading,
    handleUpdateLimit,
    handleShowCardDetails,
    handleFreezeCard,
    handleUpdatePin,
    handlePinChange,
    handleLimitChange,
  } = useCardDetails({ userCard, callbacks, address, chainId });

  const cardNumber =
    showCardDetails && decryptedCardNumber
      ? decryptedCardNumber.match(/.{1,4}/g)?.join(" ") || ""
      : userCard
      ? `**** **** **** ${userCard.last4}`
      : "";

  const cardLimit =
    userCard?.solverType === "aave-v3"
      ? creditInfo.aave?.data?.availableLimit ?? 0
      : userCard?.solverType === "euler"
      ? creditInfo.euler?.data?.availableLimit ?? 0
      : creditInfo.hypurrFi?.data?.availableLimit ?? 0;

  // Pin validation logic
  const isPinValid = newPin.length > 0 && newPin === confirmPin;

  // Limit validation logic
  const isLimitValid = inputLimit.length > 0;
  const isLimitChanged = inputLimit !== limit;

  const handleSavePin = () => {
    if (isPinValid && !isLoading) {
      handleUpdatePin(newPin);
      // Reset pin inputs after successful save
      setNewPin("");
      setConfirmPin("");
    }
  };

  const handleSaveLimit = () => {
    if (isLimitValid && isLimitChanged && !isLoading) {
      handleUpdateLimit(inputLimit);
    }
  };

  // Handle view transition with fade effect
  const handleViewChange = (newView: ViewType) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView(newView);
      setIsTransitioning(false);
    }, 150);
  };

  const renderView = useMemo(() => {
    switch (currentView) {
      case "main":
        return (
          <>
            <ContentWrapper
              isHoverable
              onClick={() => handleViewChange("changePin")}
            >
              <div className="flex justify-between w-full">
                <Typography className="text-[#fff] text-sm sm:text-base">
                  Change Pin
                </Typography>
                <ChevronRightIcon />
              </div>
            </ContentWrapper>
            <ContentWrapper
              isHoverable
              onClick={() => handleViewChange("transactionLimit")}
            >
              <div className="flex justify-between w-full">
                <Typography className="text-[#fff] text-sm sm:text-base">
                  Transaction Limit{" "}
                  <span
                    className="text-[#A8ADB5] text-[14px]"
                    style={{ color: "#A8ADB5" }}
                  >
                    (${limit} currently)
                  </span>
                </Typography>
                <ChevronRightIcon />
              </div>
            </ContentWrapper>
            <ContentWrapper>
              <div className="flex justify-between w-full">
                <Typography className="text-[#fff] text-sm sm:text-base">
                  Connected Wallet
                </Typography>
                <WalletTag address={address} />
              </div>
            </ContentWrapper>
            <ContentWrapper>
              <div className="flex justify-between w-full">
                <Typography className="text-[#fff] text-sm sm:text-base">
                  Freeze Card Transactions
                </Typography>
                <FreezeCardTransactions
                  isLoading={isLoading}
                  handleFreezeCard={handleFreezeCard}
                  userCard={userCard}
                />
              </div>
            </ContentWrapper>
            <ContentWrapper>
              <div className="flex items-start gap-2 text-xs text-[#888]">
                <InfoIcon className="w-4 h-4 flex-shrink-0 mt-1" />
                <Typography className="text-[#CBCED1] text-[14px] font-medium leading-[18px] sm:leading-[20px]">
                  BrahmaFi card is not affiliated with swype.fun, AAVE, Euler,
                  HypurrFi or other Defi and issued subject to separate terms
                  provided by Issuer.
                </Typography>
              </div>
            </ContentWrapper>
          </>
        );

      case "changePin":
        return (
          <>
            <ContentWrapper>
              <div className="flex flex-col gap-[10px]">
                <Typography className="text-[rgb(168,173,181)]">
                  Change Pin
                </Typography>

                <CustomInput
                  type="password"
                  placeholder="Enter New Pin"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                />
                <CustomInput
                  type="password"
                  placeholder="Confirm New Pin"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value)}
                />
                {newPin.length > 0 && confirmPin.length > 0 && !isPinValid && (
                  <Typography className="text-[#F45562] text-sm">
                    Pins do not match
                  </Typography>
                )}
              </div>
            </ContentWrapper>

            <ContentWrapper>
              <div className="flex items-start gap-2 text-xs text-[#888]">
                <InfoIcon className="w-4 h-4 flex-shrink-0 mt-1" />
                <Typography className="text-[#CBCED1] text-[14px] font-medium leading-[18px] sm:leading-[20px]">
                  Changing your card PIN will deactivate the old one for all
                  transactions. Make sure to use your new PIN going forward.
                </Typography>
              </div>
            </ContentWrapper>

            <ContentWrapper>
              <div className="flex flex-col gap-[12px]">
                <CustomButton
                  disabled={!isPinValid || isLoading}
                  onClick={handleSavePin}
                >
                  <Typography>Save Pin</Typography>
                </CustomButton>
                <CustomButton
                  variant="secondary"
                  onClick={() => handleViewChange("main")}
                >
                  <div className="flex items-center gap-2">
                    <span className="transform rotate-180">
                      <ChevronRightIcon />
                    </span>
                    <Typography>Back</Typography>
                  </div>
                </CustomButton>
              </div>
            </ContentWrapper>
          </>
        );

      case "transactionLimit":
        return (
          <>
            <ContentWrapper>
              <div className="flex justify-between w-full">
                <Typography className="text-[#fff] text-sm sm:text-base">
                  Current Daily Transaction Limit
                </Typography>{" "}
                <Typography className="text-[#fff] text-sm sm:text-base">
                  ${limit}
                </Typography>
              </div>
            </ContentWrapper>

            <ContentWrapper>
              <div className="flex flex-col gap-[10px] w-full">
                <Typography className="text-[rgb(168,173,181)]">
                  New Daily Transaction Limit
                </Typography>
                <CustomInput
                  type="text"
                  placeholder="Enter new limit"
                  value={inputLimit}
                  onChange={handleLimitChange}
                />
              </div>
            </ContentWrapper>

            <ContentWrapper>
              <div className="flex items-start gap-2 text-xs text-[#888]">
                <InfoIcon className="w-4 h-4 flex-shrink-0 mt-1" />
                <Typography className="text-[#CBCED1] text-[14px] font-medium leading-[18px] sm:leading-[20px]">
                  When you set a new transaction limit, each purchase will be
                  capped at that amount. You won't be able to spend more than
                  the limit you choose.
                </Typography>
              </div>
            </ContentWrapper>

            <ContentWrapper>
              <div className="flex flex-col gap-[12px]">
                <CustomButton
                  disabled={!isLimitValid || !isLimitChanged || isLoading}
                  onClick={handleSaveLimit}
                >
                  <Typography>Update Daily Limit</Typography>
                </CustomButton>
                <CustomButton
                  variant="secondary"
                  onClick={() => handleViewChange("main")}
                >
                  <div className="flex items-center gap-2">
                    <span className="transform rotate-180">
                      <ChevronRightIcon />
                    </span>
                    <Typography>Back</Typography>
                  </div>
                </CustomButton>
              </div>
            </ContentWrapper>
          </>
        );

      default:
        return null;
    }
  }, [
    currentView,
    limit,
    inputLimit,
    address,
    isLoading,
    userCard,
    handleFreezeCard,
    newPin,
    confirmPin,
    isPinValid,
    isLimitValid,
    isLimitChanged,
    handleSavePin,
    handleSaveLimit,
    handleLimitChange,
  ]);

  return (
    <Modal title={title} onClose={onClose} padding="px-[4px] py-[4px] ">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Card Display */}
        <div className="px-[12px] py-[12px] flex justify-center">
          <CreditCard
            solverType={userCard?.solverType}
            number={cardNumber}
            limit={cardLimit}
            showPin={showCardDetails}
            expiry={showCardDetails ? expiry : `**/****`}
            cvv={decryptedCVC || "***"}
            isLoading={isLoading}
            onToggleViewPin={
              // if card is not active, do not allow toggling pin view
              userCard?.status === "active" ? handleShowCardDetails : undefined
            }
          />
        </div>

        {/* Card Status */}
        <div className="px-[12px] pb-[12px] flex items-center justify-between">
          <Typography
            className="text-[#A8ADB5] text-[14px] sm:text-base"
            style={{ color: "#A8ADB5" }}
          >
            Card Status
          </Typography>
          <div className="bg-[rgba(102,219,148,0.10)] text-[#66db94] rounded-[4px] px-3 py-1 text-[12px]">
            {userCard?.status === "active"
              ? "Active"
              : userCard?.status === "inactive"
              ? "Inactive"
              : userCard?.status === "locked"
              ? "Locked"
              : "Pending"}
          </div>
        </div>

        <div
          className={`rounded-[4px] flex gap-[2px] flex-col overflow-hidden transition-opacity duration-300 ease-in-out ${
            isTransitioning ? "opacity-0" : "opacity-100"
          }`}
        >
          {renderView}
        </div>
      </div>
    </Modal>
  );
}
